import { ReactElement } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import Swiper from '~/ui/components/common/Swiper/Swiper';
import { IUpsellProduct } from '~/ui/components/common/Swiper/types';
import Link from '@Hotels-at-Home/hah-fe-core/dist/components/Link';
import {
  accorTagging,
  parentHotelCompany,
  productNameFilters,
  siteName,
} from '~/constants/siteConfig';
import { dataLayerSelectItemAccor } from '@Hotels-at-Home/hah-fe-core/dist/utils';
import { useItemListName } from '@Hotels-at-Home/hah-fe-core/dist/hooks';

interface YMALCarouselProps {
  items: IUpsellProduct[];
}

const YMALCarousel = ({ items }: YMALCarouselProps): ReactElement => {
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
  const { t: tProduct } = useTranslation('', { keyPrefix: '$pages.$product' });

  const { locale } = useRouter();
  const itemListName = useItemListName('/suggestions/');

  const handleProductClick = (
    event: React.MouseEvent<HTMLElement | Element>,
    item: IUpsellProduct,
  ) => {
    if (accorTagging.accorTaggingEnabled) {
      dataLayerSelectItemAccor({
        product: item,
        affiliation: parentHotelCompany,
        brand: siteName,
        siteFilters: productNameFilters,
        index: 0,
        itemListName,
      });
    }
  };

  const renderSlide = (item: IUpsellProduct): ReactElement => (
    <Link
      className="swiper-link"
      href={`/${locale}/p/${item.url_rewrites[item.url_rewrites.length - 1].url}`}
      noClass
      onClick={ev => handleProductClick(ev, item)}
    >
      <div className="swiper-img-wrapper hover-btn-animation">
        <img
          className="swiper-img"
          src={item.thumbnail.url}
          alt={item.name}
          width={600}
          height={376}
          loading="lazy"
          data-event-name="bloc_interact"
          data-bloc-name={`ymal image ${item.name}`}
        />
        <div
          className="hover-content desktop-only"
          data-event-name="bloc_interact"
          data-bloc-name={tButton('shopNow')}
        >
          <span className="btn base-btn btn-main">
            <span>{tButton('shopNow')}</span>
          </span>
        </div>
      </div>
      <div className="swiper-slide-copy">
        <h3
          className="swiper-title"
          data-event-name="bloc_interact"
          data-bloc-name={`ymal name ${item.name}`}
        >
          {item.product_subtitle?.trim() ? `${item.name} - ${item.product_subtitle}` : item.name}
          <span className="icon-arrow" />
        </h3>
        {item.short_description && (
          <p className="swiper-description">{item.short_description.html}</p>
        )}
      </div>
    </Link>
  );

  return (
    <section className="ymal">
      <h2>{tProduct('ymal')}</h2>
      <div className="carousel-wrapper">
        <Swiper
          slides={items}
          renderSlide={renderSlide}
          slidesPerView={1.2}
          slidesPerGroup={1} // Move 'X' slides at a time
          spaceBetween={14}
          arrowBorder
          navigation // Include navigation
          pagination // Include pagination
          containerModifierClass="swiper-"
          breakpoints={{
            // when window width is >= 480px
            480: {
              slidesPerView: 1.2,
              slidesPerGroup: 1,
              spaceBetween: 10,
            },
            768: {
              slidesPerView: 3,
              slidesPerGroup: 3,
              spaceBetween: 10,
            },
            1024: {
              slidesPerView: 3,
              slidesPerGroup: 3,
              spaceBetween: 40,
            },
          }}
        />
      </div>
    </section>
  );
};

export default YMALCarousel;
